import { timeOf, todayIso } from '@shkola/api';
import { type ToneName, subjectLook, tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRightIcon, CaretRightIcon, MegaphoneIcon } from '../../design/icons';
import { OnColorProvider, StatusPill, buttonClass, useAge } from '../../design/ui';
import { gradeShort } from '../../lib/labels';
import { AbroadCard } from '../AbroadPage';
import {
  type TaskGroup,
  gradeTone,
  ground,
  useGradeFeed,
  useNow,
  useSchoolFeed,
  useTaskGroups,
} from './blocks';
import { TodoLinkTo } from './Cards';
import { EVENT_LOOK, GlassDay } from './GlassDay';
import { ON_PEDESTAL, Pedestal } from './GlassArt';
import { GlassMood } from './GlassMood';
import { type StudentContext, dayLong, dayMonthShort, metaOf, weekdayShort } from './model';

/*
 * «Скло» — the look he chose to go on with (27.10), the home as agreed after his «подумай, що на
 * головній» (docs/ux-home-student.md §13): six blocks, each answering one question in a form of its own,
 * nothing shown twice.
 *  — the greeting: «привіт,» and the name, the date; beside them what goes on now and what is next
 *    (no picture — his words);
 *  — the latest grades, the new ones marked;
 *  — the lessons as a calendar with the days of the week on top (GlassDay);
 *  — what to do: a pastel card per kind with its picture and the tasks inside;
 *  — the school: fresh news for the students and the events of two weeks;
 *  — «Як ти сьогодні?» (GlassMood).
 * The material is the one of «Aella Credit» (big blocks, corners of 32, gaps of 8), the cards of «Duomo».
 * No black — the dark is our navy.
 */

const WHITE_CARD =
  'flex min-w-0 flex-col rounded-[32px] bg-white p-5 shadow-[0_18px_40px_-28px_rgb(30_42_94/0.35)] md:p-6';

const GREETING = [
  'radial-gradient(55% 80% at 100% 0%, rgb(255 255 255 / 0.55) 0%, rgb(255 255 255 / 0) 70%)',
  'radial-gradient(45% 90% at 0% 100%, #FFE3CF 0%, rgb(255 227 207 / 0) 70%)',
  'linear-gradient(120deg, #CEC6FB 0%, #DED5FB 35%, #F1DDF4 70%, #FBE2E6 100%)',
].join(', ');

/** The title row of a white card: the name, a count and ↗ into its section. */
export function CardHead({
  title,
  count,
  to,
  label,
}: {
  title: string;
  count?: number;
  to: '/grades' | '/events' | '/homework';
  label: string;
}) {
  return (
    <header className="mb-3 flex min-h-8 items-start gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-2 pt-1">
        <h2 className="truncate font-display text-[18px] leading-6 font-semibold text-ink">
          {title}
        </h2>
        {count !== undefined && count > 0 && (
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-soft px-2 text-[12px] font-semibold text-muted">
            {count}
          </span>
        )}
      </div>
      <Link
        to={to}
        aria-label={label}
        title={label}
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-ink ring-1 ring-stroke transition-transform duration-500 ease-spring ring-inset hover:rotate-45"
      >
        <ArrowUpRightIcon size={15} weight="bold" aria-hidden />
      </Link>
    </header>
  );
}

// —— the greeting: what goes on now and what is next ——

/**
 * The frosted card beside the name: «Зараз» with what is left, «Далі» with the next lesson; `flat` — no
 * card of its own, straight on a white block («Скло 2»).
 */
export function NowCard({ ctx, flat = false }: { ctx: StudentContext; flat?: boolean }) {
  const { t } = useTranslation();
  const state = useNow(ctx);
  if (!state.ready)
    return (
      <div
        className={`h-[200px] rounded-[26px] ${flat ? 'bg-[#F5F6F9]' : 'bg-white/40'}`}
        aria-busy
      />
    );
  const { current, next } = state;
  const tint = tones[current.tone];
  const Icon = current.icon;
  const label = 'text-[11.5px] font-semibold tracking-[0.06em] text-muted uppercase';
  return (
    <div
      className={
        flat
          ? 'flex min-w-0 flex-col'
          : 'flex flex-col rounded-[26px] bg-white/70 p-5 ring-1 ring-white backdrop-blur-md ring-inset'
      }
    >
      <div className="flex items-center gap-2">
        <span className={label}>{t('studentHome.glass.now.now')}</span>
        {current.aside && (
          <span className="ml-auto">
            <StatusPill tone={current.tone} dot={current.live} size="xs">
              {current.aside}
            </StatusPill>
          </span>
        )}
      </div>
      <p className="mt-1.5 flex items-center gap-2 font-display text-[20px] leading-7 font-semibold text-ink">
        {Icon && (
          <Icon size={20} weight="fill" aria-hidden color={tint.vivid} className="shrink-0" />
        )}
        <span className="truncate">{current.title}</span>
      </p>
      {current.sub && <p className="truncate text-[13px] text-muted">{current.sub}</p>}
      {current.progress !== undefined && (
        <span
          className={`mt-3 block h-1.5 overflow-hidden rounded-full ${flat ? 'bg-[#EEF0F4]' : 'bg-white'}`}
        >
          <span
            className="block h-full rounded-full"
            style={{
              width: `${Math.round(Math.min(1, Math.max(0, current.progress)) * 100)}%`,
              backgroundColor: tint.vivid,
            }}
          />
        </span>
      )}
      <span className="my-4 block h-px bg-[rgb(30_42_94/0.08)]" />
      {next ? (
        <>
          <span className={label}>{next.label}</span>
          <div className="mt-2 flex items-center gap-3">
            <span
              className="flex h-12 w-[60px] shrink-0 items-center justify-center rounded-[14px] font-display text-[17px] font-semibold"
              style={{
                backgroundColor: tones[subjectLook(next.lesson.subject).tone].soft,
                color: tones[subjectLook(next.lesson.subject).tone].vivid,
              }}
            >
              {timeOf(next.lesson.startsAt)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[16px] leading-6 font-semibold text-ink">
                  {next.lesson.subject}
                </span>
                {next.lesson.isTest && (
                  <StatusPill tone="red" size="xs">
                    {t('studentHome.test')}
                  </StatusPill>
                )}
              </span>
              <span className="block truncate text-[13px] text-muted">
                {metaOf(t, next.lesson)}
              </span>
            </span>
          </div>
          {next.extra && <p className="mt-2.5 truncate text-[12.5px] text-muted">{next.extra}</p>}
        </>
      ) : (
        <p className="text-[14px] text-ink-soft">{state.nothing}</p>
      )}
      {state.join && (
        <Link
          to="/lesson/$lessonId"
          params={{ lessonId: state.join.id }}
          className={`mt-4 self-start ${buttonClass('primary', 'sm')}`}
        >
          {t('student.lessons.joinLesson')}
        </Link>
      )}
    </div>
  );
}

function Greeting({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const age = useAge();
  return (
    <section
      className={`rim relative grid min-w-0 grid-cols-1 items-center gap-6 overflow-hidden rounded-[32px] p-6 md:p-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,350px)] ${className}`}
      style={{ backgroundImage: GREETING }}
    >
      <div className="min-w-0">
        <p className="-ml-0.5 inline-block -rotate-2 font-hand text-[34px] leading-9 font-bold text-[#5B4F9A]">
          {t('studentHome.hi')}
        </p>
        <h1
          className={`font-display font-semibold tracking-[-0.03em] text-ink ${age === 'junior' ? 'text-[56px] leading-[60px]' : 'text-[46px] leading-[52px] md:text-[54px] md:leading-[60px]'}`}
        >
          {ctx.child.firstName}
        </h1>
        <p className="mt-2 text-[15px] leading-5 text-ink-soft">
          {dayLong(todayIso())}
          {ctx.child.class?.label && ` · ${ctx.child.class.label}`}
        </p>
      </div>
      <NowCard ctx={ctx} />
    </section>
  );
}

// —— the latest grades ——

const GRADE_ROW = 58;

function NewGrades({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const { pending, grades, unread, fresh } = useGradeFeed(ctx);
  // as many rows as the block's height takes (it fills the column under «Треба зробити»)
  const ref = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => entry && setRoom(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const rows = room > 0 ? Math.min(8, Math.max(2, Math.floor(room / GRADE_ROW))) : 4;
  return (
    <section className={`${WHITE_CARD} ${className}`}>
      <CardHead
        title={fresh ? t('studentHome.glass.newGrades') : t('studentHome.glass.grades')}
        to="/grades"
        label={t('studentHome.glass.allGrades')}
      />
      <div ref={ref} className="flex min-h-[116px] flex-1 basis-0 flex-col overflow-hidden">
        {pending ? null : grades.length === 0 ? (
          <p className="flex flex-1 items-center justify-center text-[14px] text-muted">
            {t('studentHome.glass.noGrades')}
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {grades.slice(0, rows).map((g) => {
              const tint = tones[gradeTone(g)];
              return (
                <li key={g.id}>
                  <Link
                    to="/grades"
                    className="flex h-14 items-center gap-3 rounded-[16px] px-1.5 transition-colors hover:bg-soft"
                  >
                    <span
                      className="flex size-11 shrink-0 items-center justify-center rounded-[14px] font-display text-[18px] font-semibold"
                      style={{ backgroundColor: tint.soft, color: tint.vivid }}
                    >
                      {gradeShort(t, g.value, g.special)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-[14px] leading-5 font-semibold text-ink">
                          {g.subject}
                        </span>
                        {unread.has(g.id) && (
                          <StatusPill tone="blue" size="xs">
                            {t('studentHome.glass.fresh')}
                          </StatusPill>
                        )}
                      </span>
                      <span className="block truncate text-[12.5px] leading-[18px] text-muted">
                        {g.comment
                          ? `«${g.comment}»`
                          : (g.title ?? g.topic ?? t(`journal.kinds.${g.kind}`))}
                      </span>
                    </span>
                    <span className="shrink-0 text-[12px] text-muted">{dayMonthShort(g.date)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

// —— what to do: a card per kind, the tasks inside ——

/** The tasks of a card on its white panel: the first few, then «ще N» into the section. */
export function TaskRows({ group, max }: { group: TaskGroup; max: number }) {
  const { t } = useTranslation();
  const shown = group.rows.slice(0, max);
  const hidden = group.rows.length - shown.length;
  return (
    <div className="mt-auto flex flex-col rounded-[18px] bg-white/85 p-1.5">
      {shown.map((r) => (
        <TodoLinkTo
          key={r.key}
          link={r.link}
          className="group flex h-[46px] shrink-0 items-center gap-2.5 rounded-[12px] px-2.5 transition-colors hover:bg-white"
        >
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full"
            style={{
              backgroundColor: r.subject
                ? tones[subjectLook(r.subject).tone].icon
                : tones[group.tone].icon,
            }}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] leading-[18px] font-semibold text-ink">
              {r.title}
            </span>
            <span className="block truncate text-[12px] leading-4 text-muted">{r.sub}</span>
          </span>
          <CaretRightIcon
            size={12}
            weight="bold"
            aria-hidden
            className="shrink-0 text-faint transition-transform group-hover:translate-x-0.5 group-hover:text-ink"
          />
        </TodoLinkTo>
      ))}
      {hidden > 0 && (
        <TodoLinkTo
          link={group.link}
          className="flex h-[30px] shrink-0 items-center gap-1 px-2.5 text-[12.5px] font-semibold"
        >
          <span style={{ color: tones[group.tone].vivid }}>
            {t('studentHome.glass.board.more', { count: hidden })}
          </span>
          <CaretRightIcon size={11} weight="bold" aria-hidden color={tones[group.tone].vivid} />
        </TodoLinkTo>
      )}
    </div>
  );
}

/** A kind of task as a «Duomo» card: its name and count, its thing on a pedestal, the tasks inside. */
function TaskCard({
  group,
  tall = false,
  className = '',
}: {
  group: TaskGroup;
  /** two rows of the grid high: more tasks inside */
  tall?: boolean;
  className?: string;
}) {
  const tint = tones[group.tone];
  const Art = group.art;
  return (
    <div
      className={`relative isolate flex min-w-0 flex-col overflow-hidden rounded-[24px] p-1.5 ring-2 ring-white ring-inset ${className}`}
    >
      <span
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{ backgroundImage: ground(group.tone) }}
      />
      <div className="flex items-start gap-1 pt-2 pl-3">
        <TodoLinkTo link={group.link} className="min-w-0 flex-1 pt-1">
          <span className="block text-[13.5px] leading-5 font-semibold" style={{ color: tint.ink }}>
            {group.title}
          </span>
          {group.count > 0 && (
            <span
              className="block font-display text-[34px] leading-10 font-semibold tracking-[-0.02em]"
              style={{ color: tint.ink }}
            >
              {group.count}
            </span>
          )}
        </TodoLinkTo>
        <span className="relative -mt-1 -mb-2 block h-[78px] w-[100px] shrink-0">
          <Pedestal
            tone={group.tone}
            thing={<Art tone={group.tone} {...ON_PEDESTAL} />}
            className="absolute inset-0 h-full w-full"
          />
        </span>
      </div>
      {group.text ? (
        <p className="mt-auto rounded-[18px] bg-white/85 p-3.5 text-[13.5px] text-ink-soft">
          {group.text}
        </p>
      ) : (
        <TaskRows group={group} max={tall ? 5 : 2} />
      )}
    </div>
  );
}

/** Where the cards stand in a 2×2 grid when there are up to four (the first is the most urgent). */
const SPANS: Record<number, ReadonlyArray<string>> = {
  1: ['sm:col-span-2 sm:row-span-2'],
  2: ['sm:row-span-2', 'sm:row-span-2'],
  3: ['sm:row-span-2', '', ''],
  4: ['', '', '', ''],
};
const TALL: Record<number, number> = { 1: 1, 2: 2, 3: 1 };

function TodoBoard({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const { groups } = useTaskGroups(ctx);
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: true });
  const many = groups.length > 4;
  const total = groups.filter((g) => g.key !== 'done').reduce((n, g) => n + g.count, 0);
  const measure = () => {
    const el = ref.current;
    if (el)
      setEdge({
        start: el.scrollLeft <= 4,
        end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
      });
  };
  const keys = groups.map((g) => g.key).join(',');
  useLayoutEffect(() => {
    // the cards come one by one as the data loads: start the line from the first one again
    if (ref.current && keys) ref.current.scrollLeft = 0;
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [keys]);
  const slide = (dir: number) =>
    ref.current?.scrollBy({ left: dir * (ref.current.clientWidth / 2), behavior: 'smooth' });
  const arrow =
    'absolute top-1/2 z-10 hidden size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-[0_6px_18px_-6px_rgb(30_42_94/0.4)] transition-opacity sm:flex';
  // a phone: one line to swipe; a computer: a 2×2 grid, more than four — two rows that slide
  const layout = many
    ? 'grid-flow-col auto-cols-[82%] grid-rows-1 overflow-x-auto sm:auto-cols-[calc((100%-8px)/2)] sm:grid-rows-2'
    : `grid-flow-col ${groups.length === 1 ? 'auto-cols-[100%]' : 'auto-cols-[82%]'} overflow-x-auto sm:grid-flow-row sm:auto-cols-auto sm:grid-cols-2 sm:grid-rows-2 sm:overflow-visible`;
  return (
    <section className={`${WHITE_CARD} ${className}`}>
      <CardHead
        title={t('studentHome.glass.todo')}
        count={total}
        to="/homework"
        label={t('studentHome.glass.all')}
      />
      <div className="relative min-w-0">
        <div
          ref={ref}
          onScroll={measure}
          className={`no-scrollbar grid snap-x snap-mandatory gap-2 ${layout}`}
        >
          {groups.map((g, i) => (
            <TaskCard
              key={g.key}
              group={g}
              tall={!many && i < (TALL[groups.length] ?? 0)}
              className={`snap-start ${many ? '' : (SPANS[groups.length]?.[i] ?? '')}`}
            />
          ))}
        </div>
        {many && (
          <>
            <button
              type="button"
              aria-label={t('studentHome.glass.prev')}
              onClick={() => slide(-1)}
              className={`${arrow} -left-3 rotate-180 ${edge.start ? 'pointer-events-none opacity-0' : ''}`}
            >
              <CaretRightIcon size={15} weight="bold" aria-hidden />
            </button>
            <button
              type="button"
              aria-label={t('studentHome.glass.next')}
              onClick={() => slide(1)}
              className={`${arrow} -right-3 ${edge.end ? 'pointer-events-none opacity-0' : ''}`}
            >
              <CaretRightIcon size={15} weight="bold" aria-hidden />
            </button>
          </>
        )}
      </div>
    </section>
  );
}

// —— the school: fresh news and the events of two weeks ——

function SchoolBlock({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const { news, coming, ago, until } = useSchoolFeed(ctx);
  return (
    <section className={`${WHITE_CARD} ${className}`}>
      <CardHead
        title={t('studentHome.glass.school.title')}
        to="/events"
        label={t('studentHome.glass.school.all')}
      />
      {news.length > 0 && (
        <ul className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {news.map((a) => (
            <li key={a.id}>
              <Link
                to="/announcements"
                className="flex items-center gap-3 rounded-[18px] bg-[#F5F6F9] px-3 py-2.5 transition-colors hover:bg-[#EEF0F4]"
              >
                <span
                  className="flex size-9 shrink-0 items-center justify-center rounded-[12px]"
                  style={{ backgroundColor: tones.pink.soft, color: tones.pink.vivid }}
                >
                  <MegaphoneIcon size={17} weight="fill" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] leading-5 font-semibold text-ink">
                    {a.title}
                  </span>
                  <span className="block truncate text-[12.5px] leading-[18px] text-muted">
                    {[a.author, ago(a.publishedAt)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <StatusPill tone="blue" dot size="xs">
                  {t('studentHome.glass.school.news')}
                </StatusPill>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {coming.length === 0 ? (
        <p className="flex flex-1 items-center justify-center py-6 text-[14px] text-muted">
          {t('studentHome.glass.school.empty')}
        </p>
      ) : (
        <div className="grid flex-1 grid-cols-2 gap-2 xl:grid-cols-4">
          {coming.map((e) => {
            const look = EVENT_LOOK[e.kind];
            const tint = tones[look.tone];
            const Icon = look.icon;
            return (
              <Link
                key={e.id}
                to="/events"
                className="relative isolate flex min-h-[150px] min-w-0 flex-col overflow-hidden rounded-[22px] p-3.5 ring-2 ring-white transition-transform duration-300 ring-inset hover:-translate-y-0.5"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -z-10"
                  style={{ backgroundImage: ground(look.tone) }}
                />
                <span className="flex items-start justify-between gap-2">
                  <span className="flex h-[52px] w-12 shrink-0 flex-col items-center justify-center rounded-[14px] bg-white leading-none">
                    <span
                      className="font-display text-[20px] font-semibold"
                      style={{ color: tint.ink }}
                    >
                      {Number(e.startsOn.slice(8))}
                    </span>
                    <span className="mt-1 text-[11px] font-medium text-muted">
                      {weekdayShort(e.startsOn)}
                    </span>
                  </span>
                  <OnColorProvider value>
                    <StatusPill tone={look.tone} size="xs" className="max-w-[60%] truncate">
                      {until(e.startsOn, e.endsOn)}
                    </StatusPill>
                  </OnColorProvider>
                </span>
                <span className="mt-auto line-clamp-2 pt-3 text-[14px] leading-5 font-semibold text-ink">
                  {e.title}
                </span>
                <span
                  className="mt-0.5 flex min-w-0 items-center gap-1 text-[12px] leading-[18px]"
                  style={{ color: tint.ink }}
                >
                  <Icon
                    size={13}
                    weight="fill"
                    aria-hidden
                    color={tint.vivid}
                    className="shrink-0"
                  />
                  <span className="truncate">
                    {[e.atTime, e.place].filter(Boolean).join(' · ') || dayMonthShort(e.startsOn)}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// —— the page ——

export function GlassHome({ ctx }: { ctx: StudentContext }) {
  return (
    <div className="grid grid-cols-1 gap-2 xl:grid-cols-12">
      <div className="min-w-0 empty:hidden xl:col-span-12">
        <AbroadCard studentId={ctx.child.memberId} self />
      </div>
      <Greeting ctx={ctx} className="xl:col-span-8" />
      <GlassMood ctx={ctx} className="max-xl:order-6 xl:col-span-4" />
      <GlassDay ctx={ctx} className="xl:col-span-5" />
      {/* the column beside the lessons: what to do, the grades fill the rest of its height */}
      <div className="flex min-w-0 flex-col gap-2 max-xl:contents xl:col-span-7">
        <TodoBoard ctx={ctx} />
        <NewGrades ctx={ctx} className="max-xl:order-4 xl:flex-1" />
      </div>
      <SchoolBlock ctx={ctx} className="max-xl:order-5 xl:col-span-12" />
    </div>
  );
}
